// Design tokens and presets. Agents choose a preset and semantic intents, never raw CSS,
// so nothing agent-authored can reach a CSS value (which also rules out url() exfiltration).
// Sizes are px because POS runs Bootstrap 3, which sets html { font-size: 10px }.

export const INTENTS = ['neutral', 'accent', 'positive', 'warning', 'negative', 'info'] as const
export type Intent = typeof INTENTS[number]

export const SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'] as const
export type Severity = typeof SEVERITIES[number]

export interface PresetTokens {
  'color-bg': string
  'color-surface': string
  'color-surface-sunken': string
  'color-border': string
  'color-border-strong': string
  'color-text': string
  'color-text-muted': string
  'color-text-subtle': string
  'color-accent': string
  'color-accent-strong': string
  'color-accent-soft': string
  'color-accent-contrast': string
  'color-positive': string
  'color-positive-soft': string
  'color-warning': string
  'color-warning-soft': string
  'color-negative': string
  'color-negative-soft': string
  'color-info': string
  'color-info-soft': string
  'color-neutral': string
  'color-neutral-soft': string
  'severity-critical': string
  'severity-high': string
  'severity-medium': string
  'severity-low': string
  'severity-info': string
  'font-body': string
  'font-display': string
  'font-mono': string
  'text-xs': string
  'text-sm': string
  'text-md': string
  'text-lg': string
  'text-xl': string
  'text-2xl': string
  'text-3xl': string
  'text-4xl': string
  'leading-tight': string
  'leading-body': string
  'weight-regular': string
  'weight-medium': string
  'weight-bold': string
  'tracking-display': string
  /** Document and tool title; defaults to text-4xl, bold, leading 1.05 */
  'title-size'?: string
  'title-weight'?: string
  'title-leading'?: string
  'gap-xs': string
  'gap-sm': string
  'gap-md': string
  'gap-lg': string
  'gap-xl': string
  'pad-panel': string
  'pad-page': string
  'radius-sm': string
  'radius-md': string
  'radius-lg': string
  'shadow-panel': string
  'shadow-raised': string
  'motion-duration': string
  'motion-ease': string
  'chart-height-sm': string
  'chart-height-md': string
  'chart-height-lg': string
  'table-row-height': string
}

export interface ChartTheme {
  palette: string[]
  text: string
  textMuted: string
  axisLine: string
  splitLine: string
  surface: string
  fontFamily: string
  fontSize: number
  intents: Record<Intent, string>
  severities: Record<Severity, string>
}

export interface Preset {
  id: string
  label: string
  description: string
  scheme: 'light' | 'dark'
  tokens: PresetTokens
  palette: string[]
  /** Chart fills for intents when the text-safe token colours are too dark for bars */
  chartIntents?: Record<Intent, string>
}

const FONT_BODY = '"Inter", "Oxygen", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
const FONT_MONO = 'ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace'

const lightColors = {
  'color-bg': '#f5f7fb',
  'color-surface': '#ffffff',
  'color-surface-sunken': '#eef2f8',
  'color-border': '#e2e8f0',
  'color-border-strong': '#cbd5e1',
  'color-text': '#152033',
  'color-text-muted': '#56657d',
  'color-text-subtle': '#8492a8',
  'color-accent': '#3c8bd9',
  'color-accent-strong': '#256fbd',
  'color-accent-soft': '#e8f1fc',
  'color-accent-contrast': '#ffffff',
  'color-positive': '#15936a',
  'color-positive-soft': '#e2f5ee',
  'color-warning': '#c97a0a',
  'color-warning-soft': '#fdf1dc',
  'color-negative': '#d0413c',
  'color-negative-soft': '#fce8e7',
  'color-info': '#2f7fd0',
  'color-info-soft': '#e6f0fb',
  'color-neutral': '#64748b',
  'color-neutral-soft': '#eef2f7',
  'severity-critical': '#b42318',
  'severity-high': '#e2572b',
  'severity-medium': '#e0a106',
  'severity-low': '#3c8bd9',
  'severity-info': '#8492a8'
} as const

const SPEC_PRESETS: Record<string, Preset> = {
  narrative: {
    id: 'narrative',
    label: 'Narrative',
    description: 'Spacious and editorial, for audits and written reports',
    scheme: 'light',
    palette: ['#3c8bd9', '#14b8a6', '#8b5cf6', '#f59e0b', '#ef5d5d', '#0ea5e9', '#84cc16', '#ec4899'],
    tokens: {
      ...lightColors,
      'font-body': FONT_BODY,
      'font-display': FONT_BODY,
      'font-mono': FONT_MONO,
      'text-xs': '12px',
      'text-sm': '14px',
      'text-md': '16px',
      'text-lg': '18px',
      'text-xl': '22px',
      'text-2xl': '28px',
      'text-3xl': '36px',
      'text-4xl': '48px',
      'leading-tight': '1.2',
      'leading-body': '1.6',
      'weight-regular': '400',
      'weight-medium': '500',
      'weight-bold': '700',
      'tracking-display': '-0.02em',
      'gap-xs': '6px',
      'gap-sm': '12px',
      'gap-md': '20px',
      'gap-lg': '32px',
      'gap-xl': '48px',
      'pad-panel': '28px',
      'pad-page': '40px',
      'radius-sm': '6px',
      'radius-md': '12px',
      'radius-lg': '18px',
      'shadow-panel': '0 1px 2px rgba(16, 24, 40, 0.04), 0 12px 32px -16px rgba(16, 24, 40, 0.14)',
      'shadow-raised': '0 2px 4px rgba(16, 24, 40, 0.06), 0 20px 40px -20px rgba(16, 24, 40, 0.22)',
      'motion-duration': '420ms',
      'motion-ease': 'cubic-bezier(0.2, 0.8, 0.2, 1)',
      'chart-height-sm': '200px',
      'chart-height-md': '300px',
      'chart-height-lg': '420px',
      'table-row-height': '44px'
    }
  },
  dense: {
    id: 'dense',
    label: 'Dense',
    description: 'Compact and data-first, for dashboards and operational reports',
    scheme: 'light',
    palette: ['#2f7fd0', '#0d9488', '#7c3aed', '#d97706', '#dc4b4b', '#0284c7', '#65a30d', '#db2777'],
    tokens: {
      ...lightColors,
      'color-bg': '#f3f5f9',
      'font-body': FONT_BODY,
      'font-display': FONT_BODY,
      'font-mono': FONT_MONO,
      'text-xs': '11px',
      'text-sm': '13px',
      'text-md': '14px',
      'text-lg': '16px',
      'text-xl': '18px',
      'text-2xl': '22px',
      'text-3xl': '28px',
      'text-4xl': '34px',
      'leading-tight': '1.2',
      'leading-body': '1.45',
      'weight-regular': '400',
      'weight-medium': '500',
      'weight-bold': '650',
      'tracking-display': '-0.01em',
      'gap-xs': '4px',
      'gap-sm': '8px',
      'gap-md': '12px',
      'gap-lg': '16px',
      'gap-xl': '24px',
      'pad-panel': '16px',
      'pad-page': '20px',
      'radius-sm': '4px',
      'radius-md': '8px',
      'radius-lg': '10px',
      'shadow-panel': '0 1px 2px rgba(16, 24, 40, 0.06)',
      'shadow-raised': '0 4px 12px -4px rgba(16, 24, 40, 0.16)',
      'motion-duration': '280ms',
      'motion-ease': 'cubic-bezier(0.2, 0.8, 0.2, 1)',
      'chart-height-sm': '160px',
      'chart-height-md': '240px',
      'chart-height-lg': '340px',
      'table-row-height': '34px'
    }
  },
  executive: {
    id: 'executive',
    label: 'Executive',
    description: 'Calm and headline-first, for monthly summaries and owner reviews',
    scheme: 'light',
    palette: ['#1f3b73', '#2a9d8f', '#b8860b', '#6c757d', '#d9603b', '#457b9d', '#6a994e', '#8e5ad6'],
    tokens: {
      ...lightColors,
      'color-bg': '#f7f6f3',
      'color-surface-sunken': '#f0eee9',
      'color-border': '#e6e2da',
      'color-border-strong': '#cfc8ba',
      'color-text': '#1b2433',
      'color-accent': '#1f3b73',
      'color-accent-strong': '#152a55',
      'color-accent-soft': '#e7ecf6',
      'font-body': FONT_BODY,
      'font-display': FONT_BODY,
      'font-mono': FONT_MONO,
      'text-xs': '12px',
      'text-sm': '14px',
      'text-md': '16px',
      'text-lg': '19px',
      'text-xl': '24px',
      'text-2xl': '32px',
      'text-3xl': '42px',
      'text-4xl': '56px',
      'leading-tight': '1.15',
      'leading-body': '1.6',
      'weight-regular': '400',
      'weight-medium': '500',
      'weight-bold': '700',
      'tracking-display': '-0.025em',
      'gap-xs': '8px',
      'gap-sm': '14px',
      'gap-md': '24px',
      'gap-lg': '40px',
      'gap-xl': '56px',
      'pad-panel': '32px',
      'pad-page': '48px',
      'radius-sm': '6px',
      'radius-md': '10px',
      'radius-lg': '16px',
      'shadow-panel': '0 1px 2px rgba(27, 36, 51, 0.04)',
      'shadow-raised': '0 12px 32px -18px rgba(27, 36, 51, 0.25)',
      'motion-duration': '520ms',
      'motion-ease': 'cubic-bezier(0.16, 1, 0.3, 1)',
      'chart-height-sm': '220px',
      'chart-height-md': '320px',
      'chart-height-lg': '440px',
      'table-row-height': '46px'
    }
  },
  midnight: {
    id: 'midnight',
    label: 'Midnight',
    description: 'Dark and high-contrast, for wall screens and dim back offices',
    scheme: 'dark',
    palette: ['#5aa9f0', '#2dd4bf', '#a78bfa', '#fbbf24', '#f87171', '#38bdf8', '#a3e635', '#f472b6'],
    tokens: {
      'color-bg': '#0b1220',
      'color-surface': '#111a2b',
      'color-surface-sunken': '#0d1524',
      'color-border': '#1f2a3d',
      'color-border-strong': '#2c3a52',
      'color-text': '#e6edf7',
      'color-text-muted': '#a3b1c6',
      'color-text-subtle': '#7d8ea8',
      'color-accent': '#5aa9f0',
      'color-accent-strong': '#8cc3f6',
      'color-accent-soft': '#15283f',
      'color-accent-contrast': '#0b1220',
      'color-positive': '#34c38f',
      'color-positive-soft': '#0f2c24',
      'color-warning': '#f0a93b',
      'color-warning-soft': '#33240d',
      'color-negative': '#f07167',
      'color-negative-soft': '#3a1917',
      'color-info': '#5aa9f0',
      'color-info-soft': '#13263b',
      'color-neutral': '#94a3b8',
      'color-neutral-soft': '#1a2436',
      'severity-critical': '#ff6b5e',
      'severity-high': '#ff8a4c',
      'severity-medium': '#f5c04a',
      'severity-low': '#5aa9f0',
      'severity-info': '#8a9bb4',
      'font-body': FONT_BODY,
      'font-display': FONT_BODY,
      'font-mono': FONT_MONO,
      'text-xs': '12px',
      'text-sm': '14px',
      'text-md': '15px',
      'text-lg': '18px',
      'text-xl': '22px',
      'text-2xl': '28px',
      'text-3xl': '38px',
      'text-4xl': '52px',
      'leading-tight': '1.15',
      'leading-body': '1.5',
      'weight-regular': '400',
      'weight-medium': '500',
      'weight-bold': '650',
      'tracking-display': '-0.015em',
      'gap-xs': '6px',
      'gap-sm': '10px',
      'gap-md': '16px',
      'gap-lg': '24px',
      'gap-xl': '32px',
      'pad-panel': '20px',
      'pad-page': '24px',
      'radius-sm': '6px',
      'radius-md': '10px',
      'radius-lg': '14px',
      'shadow-panel': '0 0 0 1px rgba(255, 255, 255, 0.02)',
      'shadow-raised': '0 12px 32px -16px rgba(0, 0, 0, 0.6)',
      'motion-duration': '360ms',
      'motion-ease': 'cubic-bezier(0.2, 0.8, 0.2, 1)',
      'chart-height-sm': '180px',
      'chart-height-md': '280px',
      'chart-height-lg': '400px',
      'table-row-height': '38px'
    }
  },
  print: {
    id: 'print',
    label: 'Print',
    description: 'High contrast, no shadows or motion, colours that survive greyscale printing',
    scheme: 'light',
    palette: ['#0b5394', '#c0392b', '#1e7b4f', '#7d3c98', '#a35200', '#2c3e50', '#6f5f00', '#555555'],
    tokens: {
      ...lightColors,
      'color-bg': '#ffffff',
      'color-surface-sunken': '#f4f4f4',
      'color-border': '#d0d0d0',
      'color-border-strong': '#8c8c8c',
      'color-text': '#000000',
      'color-text-muted': '#3d3d3d',
      'color-text-subtle': '#5c5c5c',
      'color-accent': '#0b5394',
      'color-accent-strong': '#073763',
      'color-positive': '#1e7b4f',
      'color-warning': '#9a5b00',
      'color-negative': '#b03a2e',
      'color-info': '#0b5394',
      'color-neutral': '#4d4d4d',
      'font-body': FONT_BODY,
      'font-display': FONT_BODY,
      'font-mono': FONT_MONO,
      'text-xs': '11px',
      'text-sm': '13px',
      'text-md': '14px',
      'text-lg': '16px',
      'text-xl': '19px',
      'text-2xl': '24px',
      'text-3xl': '30px',
      'text-4xl': '38px',
      'leading-tight': '1.2',
      'leading-body': '1.5',
      'weight-regular': '400',
      'weight-medium': '500',
      'weight-bold': '700',
      'tracking-display': '0',
      'gap-xs': '4px',
      'gap-sm': '8px',
      'gap-md': '14px',
      'gap-lg': '20px',
      'gap-xl': '28px',
      'pad-panel': '14px',
      'pad-page': '0px',
      'radius-sm': '2px',
      'radius-md': '4px',
      'radius-lg': '6px',
      'shadow-panel': 'none',
      'shadow-raised': 'none',
      'motion-duration': '0ms',
      'motion-ease': 'linear',
      'chart-height-sm': '180px',
      'chart-height-md': '260px',
      'chart-height-lg': '360px',
      'table-row-height': '30px'
    }
  }
}

// Taken from the POS stylesheet (Material blue-grey, Bootstrap 3 alert pairs, Oxygen,
// square flat panels), so Max content reads as part of POS.
const POS_FONT = '"Oxygen", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
const POS_COLORS = {
  'color-bg': '#ffffff',
  'color-surface': '#ffffff',
  'color-surface-sunken': '#eceff1',
  'color-border': '#cfd8dc',
  'color-border-strong': '#90a4ae',
  'color-text': '#455964',
  'color-text-muted': '#546e7a',
  'color-text-subtle': '#78909c',
  'color-accent': '#3c8bd9',
  'color-accent-strong': '#2571bd',
  'color-accent-soft': '#e3eefa',
  'color-accent-contrast': '#ffffff',
  'color-positive': '#3c763d',
  'color-positive-soft': '#dff0d8',
  'color-warning': '#8a6d3b',
  'color-warning-soft': '#fcf8e3',
  'color-negative': '#a94442',
  'color-negative-soft': '#f2dede',
  'color-info': '#31708f',
  'color-info-soft': '#d9edf7',
  'color-neutral': '#607d8b',
  'color-neutral-soft': '#eceff1',
  'severity-critical': '#a94442',
  'severity-high': '#e65100',
  'severity-medium': '#8a6d3b',
  'severity-low': '#31708f',
  'severity-info': '#607d8b',
  'font-body': POS_FONT,
  'font-display': POS_FONT,
  'font-mono': FONT_MONO,
  'weight-regular': '400',
  // Oxygen ships 400 and 700 only
  'weight-medium': '400',
  'weight-bold': '700',
  'tracking-display': '0',
  // POS view titles are h3s, which POS's Bootstrap build sets bold at every density
  'title-size': '24px',
  'title-weight': '700',
  'title-leading': '1.1',
  'radius-sm': '2px',
  'radius-md': '0px',
  'radius-lg': '0px',
  'shadow-panel': '0 1px 1px rgba(0, 0, 0, 0.05)',
  'shadow-raised': '0 5px 10px rgba(0, 0, 0, 0.2)',
  'motion-duration': '280ms',
  'motion-ease': 'cubic-bezier(0.2, 0.8, 0.2, 1)'
} as const
const POS_PALETTE = ['#3c8bd9', '#607d8b', '#0097a7', '#ef6c00', '#689f38', '#7e57c2', '#8d6e63', '#e53935']
const POS_CHART_INTENTS: Record<Intent, string> = {
  neutral: '#607d8b', accent: '#3c8bd9', positive: '#689f38', warning: '#ef6c00', negative: '#e53935', info: '#0097a7'
}

/** Chosen by the host, never by a spec, so they are left out of presetIds() */
const POS_PRESETS: Record<string, Preset> = {
  pos: {
    id: 'pos',
    label: 'POS',
    description: 'The POS look at normal density, for reports and dashboards inside POS',
    scheme: 'light',
    palette: POS_PALETTE,
    chartIntents: POS_CHART_INTENTS,
    tokens: {
      ...POS_COLORS,
      'text-xs': '12px',
      'text-sm': '14px',
      'text-md': '16px',
      'text-lg': '18px',
      'text-xl': '22px',
      'text-2xl': '26px',
      'text-3xl': '32px',
      'text-4xl': '40px',
      'leading-tight': '1.2',
      'leading-body': '1.43',
      'gap-xs': '6px',
      'gap-sm': '10px',
      'gap-md': '15px',
      'gap-lg': '20px',
      'gap-xl': '30px',
      'pad-panel': '15px',
      'pad-page': '15px',
      'chart-height-sm': '180px',
      'chart-height-md': '270px',
      'chart-height-lg': '380px',
      'table-row-height': '40px'
    }
  },
  'pos-compact': {
    id: 'pos-compact',
    label: 'POS compact',
    description: 'The POS look, compact, for dense dashboards and tools inside POS',
    scheme: 'light',
    palette: POS_PALETTE,
    chartIntents: POS_CHART_INTENTS,
    tokens: {
      ...POS_COLORS,
      'text-xs': '11px',
      'text-sm': '13px',
      'text-md': '14px',
      'text-lg': '16px',
      'text-xl': '18px',
      'text-2xl': '22px',
      'text-3xl': '26px',
      'text-4xl': '32px',
      'leading-tight': '1.2',
      'leading-body': '1.4',
      'gap-xs': '4px',
      'gap-sm': '8px',
      'gap-md': '10px',
      'gap-lg': '15px',
      'gap-xl': '20px',
      'pad-panel': '10px',
      'pad-page': '10px',
      'chart-height-sm': '160px',
      'chart-height-md': '240px',
      'chart-height-lg': '340px',
      'table-row-height': '34px'
    }
  }
}

export const PRESETS: Record<string, Preset> = { ...SPEC_PRESETS, ...POS_PRESETS }

/** Inside POS every spec renders in the POS look; its preset only decides the density. */
export function posLook (preset?: string | null): string {
  return preset === 'dense' || preset === 'print' ? 'pos-compact' : 'pos'
}

export const DEFAULT_PRESET = 'narrative'

export const PRESET_DESCRIPTION = "Density only inside POS, where colours and fonts always follow POS: 'dense' and 'print' are compact, the rest normal"

/** Presets a spec or document may name */
export function presetIds (): string[] {
  return Object.keys(SPEC_PRESETS)
}

export function getPreset (id?: string | null): Preset {
  return (id && PRESETS[id]) || PRESETS[DEFAULT_PRESET]!
}

/** CSS custom properties for a preset, e.g. { '--max-color-bg': '#f5f7fb' } */
export function presetCssVars (id?: string | null): Record<string, string> {
  const preset = getPreset(id)
  const vars: Record<string, string> = {}
  for (const [name, value] of Object.entries(preset.tokens)) {
    vars['--max-' + name] = value
  }
  preset.palette.forEach((color, i) => {
    vars['--max-chart-' + (i + 1)] = color
  })
  return vars
}

export function chartTheme (id?: string | null): ChartTheme {
  const { tokens: t, palette, chartIntents } = getPreset(id)
  return {
    palette,
    text: t['color-text'],
    textMuted: t['color-text-muted'],
    axisLine: t['color-border-strong'],
    splitLine: t['color-border'],
    surface: t['color-surface'],
    fontFamily: t['font-body'],
    fontSize: parseInt(t['text-xs'], 10) || 12,
    intents: chartIntents ?? {
      neutral: t['color-neutral'],
      accent: t['color-accent'],
      positive: t['color-positive'],
      warning: t['color-warning'],
      negative: t['color-negative'],
      info: t['color-info']
    },
    severities: {
      critical: t['severity-critical'],
      high: t['severity-high'],
      medium: t['severity-medium'],
      low: t['severity-low'],
      info: t['severity-info']
    }
  }
}
